import React from 'react';
import {
  Flex,
  Button,
  NumberInput,
  NumberInputField,
  Textarea,
  Input,
  Icon,
  Image,
} from '@chakra-ui/react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import 'react-datepicker/dist/react-datepicker.css';
import {
  MdAdd,
  MdClose,
  MdFormatAlignLeft,
  MdLocationOn,
  MdOutlineImage,
  MdPeople,
} from 'react-icons/md';
import { RiPriceTag2Line } from 'react-icons/ri';

import { UseMutateFunction } from 'react-query';
import { useAuthContext } from '../AuthProvider';
import TextInput from './TextInput';
import InputLabel from './InputLabel';
import { Group } from '../models/group';
import Header from './Header';
import { useStorage } from '../hooks/useStorage';

interface GroupFormProps {
  addGroup: UseMutateFunction<void, unknown, Omit<Group, 'id'>, unknown>;
}

const GroupForm = ({ addGroup }: GroupFormProps) => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { uploadFile } = useStorage();
  const { register, control, watch, handleSubmit } = useForm();
  const file = watch('thumbnail');

  const uploadThumbnail = async (data: any) => {
    if (!data.thumbnail?.[0]) {
      return '';
    }
    const result = await uploadFile(
      `/group/thumbnails/${user?.uid}_${Date.now()}`,
      data.thumbnail[0]
    );
    return result;
  };

  const handleClick = handleSubmit(async (data) => {
    const group = {
      name: data.name,
      description: data.description,
      location: data.location,
      total: Number(data.total),
      owner: user?.uid!,
      thumbnail: await uploadThumbnail(data),
      isPrivate: false,
      members: [user?.uid!],
      boardGames: [],
    };
    await addGroup(group);
    navigate(-1);
  });

  return (
    <Flex flexDir="column" width="100%">
      <Flex
        pos="relative"
        h="100%"
        flexDirection="column"
        align="center"
        px="1rem"
      >
        <Header title="모임 생성" rightIcon={MdClose} rightTo={-1} />

        <TextInput
          icon={RiPriceTag2Line}
          label="모임 이름"
          required
          placeholder="모임 이름을 작성해주세요."
          register={register}
          fieldKey="name"
        />
        <Flex flexDir="column" alignItems="start" width="100%" my="28px">
          <InputLabel icon={MdFormatAlignLeft} label="모임 소개" required />
          <Controller
            control={control}
            name="description"
            render={({ field }) => (
              <Textarea
                value={field.value}
                onChange={field.onChange}
                placeholder="모임에 대한 설명을 작성해주세요."
                focusBorderColor="#515151"
                p="1rem"
              />
            )}
          />
        </Flex>
        <TextInput
          icon={MdLocationOn}
          label="모임 위치"
          required
          placeholder="모임 위치를 작성해주세요."
          register={register}
          fieldKey="location"
        />
        <Flex alignItems="center" justify="space-between" width="100%" m="28px">
          <InputLabel icon={MdPeople} label="멤버 인원" required />
          <Controller
            control={control}
            name="total"
            render={({ field }) => (
              <NumberInput
                focusBorderColor="#515151"
                p="1rem"
                value={field.value}
                onChange={field.onChange}
              >
                <NumberInputField />
              </NumberInput>
            )}
          />
        </Flex>
        <Flex flexDir="column" alignItems="start" width="100%" mb="50px">
          <InputLabel icon={MdOutlineImage} label="대표 이미지" />

          <Flex
            border="1px solid #F0F0F0"
            borderRadius="8px"
            minH="208px"
            width="100%"
            position="relative"
            justifyContent="center"
            alignItems="center"
            overflow="hidden"
          >
            <Icon as={MdAdd} boxSize="28px" color="#C6C6C6" />
            {file && file[0] && (
              <Image
                position="absolute"
                zIndex={9}
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                maxH="208px"
                src={URL.createObjectURL(file[0])}
                alt={file[0].name}
              />
            )}
            <Input
              {...register('thumbnail')}
              position="absolute"
              zIndex={10}
              left="0"
              right="0"
              top="0"
              bottom="0"
              minH="208px"
              type="file"
              accept="image/*"
              aria-hidden="true"
              opacity="0"
            />
          </Flex>
        </Flex>
      </Flex>
      <Flex borderTop="1px solid #D9D9D9" pt="12px" pb="34px" px="1rem">
        <Button
          onClick={handleClick}
          width="100%"
          py="14px"
          borderRadius="40px"
          color="white"
          bgColor="#D84E46"
          _active={{
            bgColor: '#AB170F',
          }}
          _hover={{
            bgColor: '#AB170F',
          }}
        >
          생성하기
        </Button>
      </Flex>
    </Flex>
  );
};

export default GroupForm;
